import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import QuickActions from '../components/QuickActions';
import ProjectsSection from '../components/ProjectsSection';
import { useTeam } from '../contexts/TeamContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, DollarSign, TrendingUp, Receipt, Heart, Users, Briefcase, Eye, EyeOff } from 'lucide-react';

const Financial = () => {
  const { currentTeam } = useTeam();
  const [expenses, setExpenses] = useState([]);
  const [fundraisers, setFundraisers] = useState([]);
  const [loading, setLoading] = useState(false);

  const totalExpenses = expenses.reduce((sum, expense) => sum + expense.amount, 0);
  const totalRaised = fundraisers.reduce((sum, fund) => sum + fund.currentAmount, 0);

  if (!currentTeam) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto fade-in">
          <h1 className="text-4xl font-bold mb-6 text-gray-900">Financial</h1>
          <Card className="p-8">
            <p className="text-gray-600">Please select a team to view financial information.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto fade-in">
        {/* Header with Quick Actions */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-4xl font-bold text-gray-900">Financial</h1>
          <QuickActions position="top" />
        </div>

        {/* Summary Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <Card className="p-6 border-2">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-red-100 rounded-lg">
                <Receipt className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Expenses</p>
                <p className="text-3xl font-bold text-gray-900">${totalExpenses.toFixed(2)}</p>
              </div>
            </div>
          </Card>
          <Card className="p-6 border-2">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Raised</p>
                <p className="text-3xl font-bold text-gray-900">${totalRaised.toFixed(2)}</p>
              </div>
            </div>
          </Card>
        </div>

        <Tabs defaultValue="expenses" className="space-y-6">
          <TabsList>
            <TabsTrigger value="expenses">
              <Receipt className="w-4 h-4 mr-2" />
              Expenses
            </TabsTrigger>
            <TabsTrigger value="fundraisers">
              <Heart className="w-4 h-4 mr-2" />
              Fundraisers
            </TabsTrigger>
            <TabsTrigger value="projects">
              <Briefcase className="w-4 h-4 mr-2" />
              Projects
            </TabsTrigger>
          </TabsList>

          {/* Expenses Tab */}
          <TabsContent value="expenses">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Expenses</h2>
                <AddExpenseDialog expenses={expenses} setExpenses={setExpenses} />
              </div>
              {expenses.length === 0 ? (
                <div className="text-center py-12 text-gray-900">
                  <Receipt className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="font-medium">No expenses tracked yet.</p>
                  <p className="text-sm mt-2 text-gray-600">Track medical bills, care costs, and other expenses</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {expenses.map(expense => (
                    <Card key={expense.id} className="p-4 border-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-gray-900">{expense.category}</p>
                          <p className="text-sm text-gray-600 mt-1">{expense.description}</p>
                          <p className="text-xs text-gray-500 mt-1">{expense.date}</p>
                        </div>
                        <p className="text-lg font-bold text-gray-900">${expense.amount.toFixed(2)}</p>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Fundraisers Tab */}
          <TabsContent value="fundraisers">
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Fundraising Campaigns</h2>
                <CreateFundraiserDialog fundraisers={fundraisers} setFundraisers={setFundraisers} />
              </div>
              {fundraisers.length === 0 ? (
                <div className="text-center py-12 text-gray-900">
                  <Heart className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="font-medium">No fundraising campaigns yet.</p>
                  <p className="text-sm mt-2 text-gray-600">Create campaigns for medical expenses, care costs, or family support</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {fundraisers.map(fundraiser => (
                    <FundraiserCard key={fundraiser.id} fundraiser={fundraiser} />
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects">
            <ProjectsSection
              currentTeam={currentTeam}
              dashboardArea="financial"
              projectTypes={[
                'Insurance Claim',
                'Medicaid Application',
                'Medicare Enrollment',
                'Budget Planning',
                'Grant Application',
                'Charity Assistance',
                'Financial Aid Application',
                'Estate Planning',
                'Tax Preparation',
                'Other'
              ]}
            />
          </TabsContent>
        </Tabs>

        {/* Quick Actions - Bottom */}
        <div className="mt-6">
          <QuickActions position="bottom" />
        </div>
      </div>
    </Layout>
  );
};

// Fundraiser Card Component
const FundraiserCard = ({ fundraiser }) => {
  const progress = (fundraiser.currentAmount / fundraiser.goalAmount) * 100;
  
  return (
    <Card className="p-6 border-2">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-xl font-bold text-gray-900 mb-2">{fundraiser.title}</h3>
          <p className="text-gray-700 mb-3">{fundraiser.description}</p>
          
          {/* Visibility Badge */}
          <div className="flex items-center gap-2 mb-3">
            {fundraiser.isPublic ? (
              <Badge className="bg-blue-100 text-blue-900 border-blue-300">
                <Users className="w-3 h-3 mr-1" />
                Public Campaign
              </Badge>
            ) : (
              <Badge className="bg-gray-100 text-gray-900 border-gray-300">
                <Eye className="w-3 h-3 mr-1" />
                Private - Inner Circle Only
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-2xl font-bold text-gray-900">${fundraiser.currentAmount.toFixed(2)}</span>
          <span className="text-sm text-gray-600">of ${fundraiser.goalAmount.toFixed(2)} goal</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className="bg-green-600 h-3 rounded-full transition-all duration-300"
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
        <p className="text-sm text-gray-600 mt-1">{progress.toFixed(1)}% funded</p>
      </div>

      {/* Donor Count */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 text-gray-600">
          <Users className="w-4 h-4" />
          <span>{fundraiser.donorCount || 0} donors</span>
        </div>
        {fundraiser.allowAnonymous && (
          <span className="text-gray-500 text-xs">Anonymous donations enabled</span>
        )}
      </div>
    </Card>
  );
};

// Add Expense Dialog
const AddExpenseDialog = ({ expenses, setExpenses }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    category: '',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  const categories = [
    'Medical Bills',
    'Medications',
    'Home Care',
    'Medical Equipment',
    'Transportation',
    'Legal Fees',
    'Household',
    'Other'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    const newExpense = {
      id: Date.now(),
      ...formData,
      amount: parseFloat(formData.amount)
    };
    setExpenses([...expenses, newExpense]);
    toast.success('Expense added successfully');
    setOpen(false);
    setFormData({ category: '', amount: '', description: '', date: new Date().toISOString().split('T')[0] });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" />
          Add Expense
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Expense</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="category" className="text-gray-900">Category *</Label>
            <select
              id="category"
              className="w-full border-2 border-gray-300 rounded-md px-3 py-2 mt-1 text-gray-900"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              required
            >
              <option value="">Select category...</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="amount" className="text-gray-900">Amount *</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder="0.00"
              className="text-gray-900 border-2"
              required
            />
          </div>

          <div>
            <Label htmlFor="date" className="text-gray-900">Date *</Label>
            <Input
              id="date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="text-gray-900 border-2"
              required
            />
          </div>

          <div>
            <Label htmlFor="description" className="text-gray-900">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional details..."
              rows={2}
              className="text-gray-900 border-2"
            />
          </div>

          <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            Add Expense
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Create Fundraiser Dialog
const CreateFundraiserDialog = ({ fundraisers, setFundraisers }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    goalAmount: '',
    isPublic: false,
    allowAnonymous: true,
    shareWithSupportTeam: false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const newFundraiser = {
      id: Date.now(),
      ...formData,
      goalAmount: parseFloat(formData.goalAmount),
      currentAmount: 0,
      donorCount: 0,
      createdAt: new Date().toISOString()
    };
    setFundraisers([...fundraisers, newFundraiser]);
    toast.success('Fundraising campaign created successfully');
    setOpen(false);
    setFormData({ 
      title: '', 
      description: '', 
      goalAmount: '', 
      isPublic: false, 
      allowAnonymous: true,
      shareWithSupportTeam: false
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" />
          Create Campaign
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Fundraising Campaign</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title" className="text-gray-900">Campaign Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Help with Medical Expenses"
              className="text-gray-900 border-2"
              required
            />
          </div>

          <div>
            <Label htmlFor="description" className="text-gray-900">Description *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Explain what the funds will be used for..."
              rows={4}
              className="text-gray-900 border-2"
              required
            />
          </div>

          <div>
            <Label htmlFor="goal" className="text-gray-900">Fundraising Goal *</Label>
            <Input
              id="goal"
              type="number"
              step="0.01"
              value={formData.goalAmount}
              onChange={(e) => setFormData({ ...formData, goalAmount: e.target.value })}
              placeholder="0.00"
              className="text-gray-900 border-2"
              required
            />
          </div>

          <div className="border-t-2 pt-4 space-y-3">
            <Label className="text-gray-900 font-semibold">Campaign Settings</Label>
            
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isPublic}
                onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-gray-900">Public Campaign</div>
                <div className="text-sm text-gray-600">Anyone can view and donate (shareable link)</div>
              </div>
            </label>

            {!formData.isPublic && (
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.shareWithSupportTeam}
                  onChange={(e) => setFormData({ ...formData, shareWithSupportTeam: e.target.checked })}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-gray-900">Share with Support Team</div>
                  <div className="text-sm text-gray-600">Support team members can see and contribute</div>
                </div>
              </label>
            )}

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.allowAnonymous}
                onChange={(e) => setFormData({ ...formData, allowAnonymous: e.target.checked })}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-gray-900">Allow Anonymous Donations</div>
                <div className="text-sm text-gray-600">Donors can choose not to appear on the donor list</div>
              </div>
            </label>
          </div>

          <div className="bg-blue-50 p-3 rounded-lg border-2 border-blue-200">
            <p className="text-sm text-gray-900">
              <strong>Note:</strong> Donations will be processed through Stripe. You'll receive a shareable link after creating the campaign.
            </p>
          </div>

          <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            Create Campaign
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Financial;
